import { useCallback, useState } from 'react';
import { useIntl } from 'react-intl';
import { mapRepliconDateToMidnightUTCString } from '~/modules/common/dates/convert';
import {
  AllocationDistributionMode,
  ResourceAllocationScheduleRulesUpdateBehaviour
} from '~/types';
import { useSnackbar } from '~/modules/common/snackbar';
import { useDialogState } from '~/modules/common/hooks';
import { getHoursValueByPeriod } from '../../AllocationByPeriod';

export const useSetResourceAllocationOnSubmit = ({
  existingResourceAllocation,
  userUri,
  onClose,
  onUpdateResourceAllocation
}) => {
  const { formatMessage } = useIntl();
  const { showSnackbar } = useSnackbar();

  const {
    open: isSmartFitOpen,
    closeDialog: closeSmartFitDialog,
    openDialog: openSmartFitDialog
  } = useDialogState(false);

  const [smartFitSubmission, setSmartFitSubmission] = useState(null);

  const submitAllocation = useCallback(
    async ({ allocationDetails, distributionMode }) => {
      await onUpdateResourceAllocation({
        allocation: {
          id: existingResourceAllocation.id,
          user: { userUri },
          allocationHoursInDateRangeInput: {
            dateRange: allocationDetails.allocationDateRange,
            hoursPerPeriod: allocationDetails.totalAllocatedHours,
            periodType: allocationDetails.periodType,
            distributionMode
          },
          allocationScheduleRulesUpdateBehaviour: allocationDetails.isClearAllocationChecked
            ? ResourceAllocationScheduleRulesUpdateBehaviour.ReplaceAndClearOutsideRange
            : ResourceAllocationScheduleRulesUpdateBehaviour.ReplaceOnly
        },
        existingResourceAllocation
      });

      showSnackbar &&
        showSnackbar(
          formatMessage({
            id: 'setResourceAllocationDialog.snackbarSuccessMessage'
          })
        );

      onClose();
    },
    [
      existingResourceAllocation,
      userUri,
      formatMessage,
      onClose,
      onUpdateResourceAllocation,
      showSnackbar
    ]
  );

  const handleSetAllocation = useCallback(
    async (_, distributionMode) => {
      closeSmartFitDialog();
      await submitAllocation({
        allocationDetails: smartFitSubmission,
        distributionMode
      });
    },
    [smartFitSubmission, submitAllocation, closeSmartFitDialog]
  );

  const onFormSubmit = useCallback(
    values => {
      const totalAllocatedHours =
        parseFloat(
          getHoursValueByPeriod({
            selectedAllocationPeriod: values.selectedAllocationPeriod,
            hoursForEntirePeriod: values.hoursForEntirePeriod,
            hoursPerMonth: values.hoursPerMonth,
            hoursPerWeek: values.hoursPerWeek
          })
        ) || 0;

      const allocationDetails = {
        totalAllocatedHours,
        allocationDateRange: {
          startDate: mapRepliconDateToMidnightUTCString(values.startDate),
          endDate: mapRepliconDateToMidnightUTCString(values.endDate)
        },
        periodType: values.selectedAllocationPeriod,
        isClearAllocationChecked: values.isClearAllocationChecked
      };

      if (totalAllocatedHours === 0) {
        submitAllocation({
          allocationDetails,
          distributionMode: AllocationDistributionMode.FitsAvailableHours
        });
      } else {
        setSmartFitSubmission(allocationDetails);
        openSmartFitDialog();
      }
    },
    [submitAllocation, openSmartFitDialog]
  );

  return {
    onFormSubmit,
    handleSetAllocation,
    isSmartFitOpen,
    smartFitSubmission,
    closeSmartFitDialog
  };
};
